// 장비 상태 색상·라벨 - 모니터링 보드·일중 타임라인·이벤트 목록에서 공통으로 쓴다(서버 runSt 값 기준).
// 하드코딩 hex 대신 테마 팔레트 키를 쓰되, 상태별로 쓸만한 농도를 직접 고른다.
import { alpha } from '@mui/material/styles';

export const EQP_STATE_LABEL = {
  RUNNING:   '운전',
  HOLD:      '일시정지',
  IDLE:      '정지',
  ALARM:     '알람',
  EMERGENCY: '비상정지',
  OFFLINE:   '오프라인',
};

// 보드 상단 상태 칩을 그릴 순서
export const EQP_STATE_ORDER = ['RUNNING', 'HOLD', 'IDLE', 'ALARM', 'EMERGENCY', 'OFFLINE'];

// 이벤트 typ 라벨 — PROTOCOL.md §2.3 의 STATE/ALARM/PROGRAM/ONLINE(장비 통신)
export const EVENT_TYP_LABEL = { STATE: '상태', ALARM: '알람', PROGRAM: '프로그램', ONLINE: '통신' };

const MODE_LABEL = { MEM: '자동', MDI: 'MDI', EDIT: '편집', HND: '핸들', JOG: '조그', REF: '원점복귀', RMT: 'DNC' };

// 이벤트 detail 은 객체(JSON)로 온다 — 화면에 그대로 넣으면 React 가 죽으므로 종류별로 한 줄 글로 바꾼다.
export const describeEventDetail = (typ, val, detail) => {
  if (!detail || typeof detail !== 'object') return detail ? String(detail) : '';
  if (typ === 'ALARM') return [detail.code, detail.text].filter((v) => v !== undefined && v !== null && v !== '').join(' ');
  if (typ === 'PROGRAM') return [detail.main ? `메인 ${detail.main}` : '', detail.name || ''].filter(Boolean).join(' · ');
  if (typ === 'ONLINE') return val === 'OFF' && detail.error ? `끊김: ${detail.error}` : '';
  if (typ === 'STATE') return detail.mode ? `모드 ${MODE_LABEL[detail.mode] || detail.mode}` : '';
  return '';
};

// null 이거나 stale(에이전트 수신 지연) 이면 화면에서는 "데이터 없음"으로 통일해서 보여 준다
export const getEqpStateLabel = (runSt, stale) => {
  if (stale || !runSt) return '데이터 없음';
  return EQP_STATE_LABEL[runSt] || runSt;
};

// 카드·타임라인 공용 색상. main=글자/테두리, bg=옅은 배경, dashed=오프라인·데이터없음 표시용 점선 여부.
export const getEqpStateStyle = (theme, runSt, stale) => {
  if (stale) {
    return { main: theme.palette.grey[500], bg: theme.palette.grey[100], label: '데이터 없음', dashed: true };
  }
  switch (runSt) {
    case 'RUNNING':
      return { main: theme.palette.success.main, bg: alpha(theme.palette.success.main, 0.12), label: '운전', dashed: false };
    case 'HOLD':
      return { main: theme.palette.warning.main, bg: alpha(theme.palette.warning.main, 0.14), label: '일시정지', dashed: false };
    case 'IDLE':
      return { main: theme.palette.grey[700], bg: theme.palette.grey[200], label: '정지', dashed: false };
    case 'ALARM':
      return { main: theme.palette.error.main, bg: alpha(theme.palette.error.main, 0.12), label: '알람', dashed: false };
    case 'EMERGENCY':
      return { main: theme.palette.error.dark, bg: alpha(theme.palette.error.dark, 0.16), label: '비상정지', dashed: false };
    case 'OFFLINE':
      return { main: theme.palette.grey[500], bg: theme.palette.grey[50], label: '오프라인', dashed: true };
    default:
      return { main: theme.palette.grey[400], bg: theme.palette.grey[100], label: '데이터 없음', dashed: true };
  }
};

// DataGrid/Chip 의 color prop(머터리얼 표준 팔레트 이름)만 필요할 때
export const getEqpStateChipColor = (runSt, stale) => {
  if (stale || !runSt) return 'default';
  switch (runSt) {
    case 'RUNNING': return 'success';
    case 'HOLD': return 'warning';
    case 'ALARM':
    case 'EMERGENCY': return 'error';
    default: return 'default';
  }
};

// "N분째" / "N시간 M분째" - stateSinceDt(상태가 바뀐 시각) 기준
export const formatSince = (stateSinceDt) => {
  if (!stateSinceDt) return null;
  const mins = Math.max(0, Math.floor((Date.now() - new Date(stateSinceDt).getTime()) / 60000));
  if (mins < 60) return `${mins}분째`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}시간 ${m}분째`;
};

// 초 단위 → "H시간 M분" (타임라인 요약 등)
export const formatSecAsHm = (sec) => {
  const s = Math.max(0, Math.round(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h <= 0) return `${m}분`;
  return `${h}시간 ${m}분`;
};

// "N분 전" - stateUpdDt(마지막 상태 수신 시각) 기준. formatSince("N분째")와는 용도가 다르다.
export const formatAgo = (dt) => {
  if (!dt) return null;
  const mins = Math.max(0, Math.floor((Date.now() - new Date(dt).getTime()) / 60000));
  if (mins < 1) return '방금 전';
  if (mins < 60) return `${mins}분 전`;
  const h = Math.floor(mins / 60);
  return `${h}시간 전`;
};

// 장비 목록(/eqpmng) 카드·그리드의 운전 상태 칩 - 모니터링 보드보다 단순한 4단계 요약.
// (운전/정지·대기/알람/끊김) + 모니터링 미사용·상태행 없음은 "수집 안 함"으로 별도 표시.
export const getEqpListStatusChip = (row) => {
  if (!row) return { label: '수집 안 함', color: 'default' };
  const hasState = row.runSt != null || row.onlineYn != null || row.stateUpdDt != null;
  if (row.monYn !== 'Y' || !hasState) {
    return { label: '수집 안 함', color: 'default' };
  }
  if (row.stale || row.onlineYn === 'N') {
    return { label: '끊김', color: 'warning' };
  }
  if (row.alarmYn === 'Y' || row.runSt === 'ALARM' || row.runSt === 'EMERGENCY') {
    return { label: '알람', color: 'error' };
  }
  if (row.runSt === 'RUNNING') {
    return { label: '운전', color: 'success' };
  }
  return { label: '정지·대기', color: 'default' };
};

// 에이전트 가동시간(초)을 사람이 읽는 형태로 - 일 단위까지 커질 수 있어서 따로 둔다.
export const formatUptime = (sec) => {
  if (sec === null || sec === undefined) return '-';
  const s = Math.max(0, Math.round(sec));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}일 ${h}시간`;
  if (h > 0) return `${h}시간 ${m}분`;
  return `${m}분`;
};
